/**
 * Utility functions for common operations
 */

import tinycolor from 'tinycolor2';
import type { Canvas as FabricCanvas } from 'fabric';
import {
  precomputeColorConfig,
  shouldTransformPixelFast,
  transformPixelFast,
  hasSimilarHueFast,
  isNeutralColorFast,
  hexToHsl,
  type PrecomputedConfig
} from './colorMath';

/**
 * HSL Delta result for color transformations
 */
export interface HSLDelta {
  deltaHue: number;      // -180 to 180 degrees
  deltaSat: number;      // -100 to 100 percent
  deltaLight: number;    // -100 to 100 percent
}

/**
 * Calculate HSL deltas between dominant color and brand color
 * @param dominantColor - The original dominant color (hex, rgb, etc.)
 * @param brandColor - The target brand color (hex, rgb, etc.)
 * @returns HSL delta values for transformation
 */
export function getHSLDelta(dominantColor: string, brandColor: string): HSLDelta {
  const dominant = tinycolor(dominantColor);
  const brand = tinycolor(brandColor);

  if (!dominant.isValid() || !brand.isValid()) {
    console.warn('[HSL Delta] Invalid color provided', { dominantColor, brandColor });
    return { deltaHue: 0, deltaSat: 0, deltaLight: 0 };
  }

  const dominantHsl = dominant.toHsl();
  const brandHsl = brand.toHsl();

  // Hue is 0-360, calculate shortest path delta
  let deltaHue = (brandHsl.h - dominantHsl.h);
  // Normalize to -180 to 180 range for shortest rotation
  if (deltaHue > 180) deltaHue -= 360;
  if (deltaHue < -180) deltaHue += 360;

  // Saturation and Lightness are 0-1, convert to percentage delta
  const deltaSat = (brandHsl.s - dominantHsl.s) * 100;
  const deltaLight = (brandHsl.l - dominantHsl.l) * 100;

  console.log(`🎨 [HSL Delta] Calculated deltas:`, {
    dominant: { h: dominantHsl.h.toFixed(1), s: (dominantHsl.s * 100).toFixed(1), l: (dominantHsl.l * 100).toFixed(1) },
    brand: { h: brandHsl.h.toFixed(1), s: (brandHsl.s * 100).toFixed(1), l: (brandHsl.l * 100).toFixed(1) },
    deltas: { H: deltaHue.toFixed(1), S: deltaSat.toFixed(1), L: deltaLight.toFixed(1) }
  });

  return {
    deltaHue,
    deltaSat,
    deltaLight
  };
}

/**
 * Apply HSL deltas to a color
 * @param originalColor - The original color to transform
 * @param delta - HSL delta values to apply
 * @returns Transformed color as hex string
 */
export function applyHSLDelta(originalColor: string, delta: HSLDelta): string {
  const color = tinycolor(originalColor);
  
  if (!color.isValid()) {
    console.warn('[HSL Delta] Invalid original color', originalColor);
    return originalColor;
  }

  const hsl = color.toHsl();

  // Apply deltas with clamping
  let newHue = (hsl.h + delta.deltaHue) % 360;
  if (newHue < 0) newHue += 360;
  
  const newSat = Math.max(0, Math.min(1, hsl.s + delta.deltaSat / 100));
  const newLight = Math.max(0, Math.min(1, hsl.l + delta.deltaLight / 100));

  const newColor = tinycolor({ h: newHue, s: newSat, l: newLight, a: hsl.a });
  
  return newColor.toHexString();
}

/**
 * Transform a color to match brand color while preserving lightness
 * This replaces the hue and saturation with brand color but keeps original lightness
 * @param originalColor - The original color to transform
 * @param brandColor - The target brand color
 * @returns Transformed color as hex string
 */
export function transformColorToBrand(originalColor: string, brandColor: string): string {
  const original = tinycolor(originalColor);
  const brand = tinycolor(brandColor);
  
  if (!original.isValid() || !brand.isValid()) {
    console.warn('[Transform Color] Invalid color provided', { originalColor, brandColor });
    return originalColor;
  }

  const originalHsl = original.toHsl();
  const brandHsl = brand.toHsl();

  // Use brand's hue and saturation, but preserve original's lightness (slightly adjusted toward brand)
  const lightnessBlend = 0.7; // 70% brand lightness, 30% original
  const newLightness = brandHsl.l * lightnessBlend + originalHsl.l * (1 - lightnessBlend);

  const newColor = tinycolor({
    h: brandHsl.h,
    s: brandHsl.s,
    l: Math.max(0.1, Math.min(0.9, newLightness)), // Clamp to avoid pure black/white
    a: originalHsl.a
  });
  
  return newColor.toHexString();
}

/**
 * Check if two colors are similar in hue (within tolerance)
 * OPTIMIZED: Uses pure math functions instead of tinycolor
 * @param color1 - First color
 * @param color2 - Second color  
 * @param tolerance - Hue tolerance in degrees (default 30)
 * @returns True if colors have similar hue
 */
export function hasSimilarHue(color1: string, color2: string, tolerance: number = 15): boolean {
  // Use optimized version without tinycolor
  return hasSimilarHueFast(color1, color2, tolerance);
}

/**
 * Convert hue delta to fabric.js HueRotation filter value
 * Fabric uses radians for hue rotation
 * @param deltaHue - Hue delta in degrees (-180 to 180)
 * @returns Rotation value in radians for fabric.filters.HueRotation
 */
export function hueToFabricRotation(deltaHue: number): number {
  // Convert degrees to radians
  // Fabric HueRotation uses -1 to 1 where 1 = 180 degrees
  return deltaHue / 180;
}

/**
 * Check if a color is neutral (grayscale)
 * Neutral colors shouldn't have hue rotation applied
 * OPTIMIZED: Uses pure math functions instead of tinycolor
 * @param color - Color to check
 * @returns True if the color is grayscale/neutral
 */
export function isNeutralColor(color: string): boolean {
  // Use optimized version without tinycolor
  return isNeutralColorFast(color);
}

/**
 * Extract dominant color from canvas by finding the most common saturated color
 * OPTIMIZED: Uses pure math functions instead of tinycolor
 * Uses color bucketing to find the most frequent hue
 * @param canvas - Fabric canvas
 * @returns Dominant color as hex string or null
 */
export function extractDominantColorFromCanvas(canvas: FabricCanvas): string | null {
  try {
    const ctx = canvas.getContext();
    const width = canvas.width ?? 100;
    const height = canvas.height ?? 100;
    
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    // Create buckets for hue ranges (36 buckets of 10 degrees each)
    const hueBuckets: Array<{ count: number; totalS: number; totalL: number; totalR: number; totalG: number; totalB: number }> = 
      Array.from({ length: 36 }, () => ({ count: 0, totalS: 0, totalL: 0, totalR: 0, totalG: 0, totalB: 0 }));
    
    // Sample pixels with larger step for performance
    const step = Math.max(1, Math.floor(Math.sqrt(width * height) / 50));
    
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const idx = (y * width + x) * 4;
        const r = data[idx];
        const g = data[idx + 1];
        const b = data[idx + 2];
        const a = data[idx + 3];
        
        // Skip transparent pixels
        if (a < 128) continue;
        
        // Inline RGB to HSL conversion (no tinycolor)
        const rN = r / 255;
        const gN = g / 255;
        const bN = b / 255;

        const max = Math.max(rN, gN, bN);
        const min = Math.min(rN, gN, bN);
        const l = (max + min) / 2;
        
        // Skip very dark/light pixels (fast reject before computing saturation)
        if (l < 0.1 || l > 0.9) continue;
        
        let s = 0;
        let h = 0;
        if (max !== min) {
          const d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          
          // Skip desaturated pixels
          if (s < 0.15) continue;
          
          switch (max) {
            case rN:
              h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6;
              break;
            case gN:
              h = ((bN - rN) / d + 2) / 6;
              break;
            default:
              h = ((rN - gN) / d + 4) / 6;
              break;
          }
          h *= 360;
        } else {
          // Achromatic - skip
          continue;
        }
        
        // Bucket by hue (0-360 -> 0-35)
        const bucketIndex = Math.floor(h / 10) % 36;
        hueBuckets[bucketIndex].count++;
        hueBuckets[bucketIndex].totalS += s;
        hueBuckets[bucketIndex].totalL += l;
        hueBuckets[bucketIndex].totalR += r;
        hueBuckets[bucketIndex].totalG += g;
        hueBuckets[bucketIndex].totalB += b;
      }
    }
    
    // Find the bucket with most pixels (weighted by saturation)
    let maxScore = 0;
    let dominantBucket = -1;
    
    for (let i = 0; i < hueBuckets.length; i++) {
      const bucket = hueBuckets[i];
      if (bucket.count === 0) continue;
      
      const avgSat = bucket.totalS / bucket.count;
      // Score = count * saturation (favor saturated colors)
      const score = bucket.count * (1 + avgSat);
      
      if (score > maxScore) {
        maxScore = score;
        dominantBucket = i;
      }
    }
    
    if (dominantBucket < 0) {
      console.log(`🎨 [Extract Dominant] No saturated colors found, using average`);
      return null;
    }
    
    const bucket = hueBuckets[dominantBucket];
    const avgR = Math.round(bucket.totalR / bucket.count);
    const avgG = Math.round(bucket.totalG / bucket.count);
    const avgB = Math.round(bucket.totalB / bucket.count);
    
    // Convert to hex using pure math
    const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
    const dominant = `#${toHex(avgR)}${toHex(avgG)}${toHex(avgB)}`;
    
    // Calculate HSL for logging
    const avgL = bucket.totalL / bucket.count;
    const avgS = bucket.totalS / bucket.count;
    
    console.log(`🎨 [Extract Dominant] Found dominant color:`, {
      hex: dominant,
      hue: dominantBucket * 10 + 5, // Approximate center of bucket
      saturation: Math.round(avgS * 100) + '%',
      lightness: Math.round(avgL * 100) + '%',
      pixelCount: bucket.count,
      hueBucket: dominantBucket * 10 + '-' + ((dominantBucket + 1) * 10)
    });
    
    return dominant;
  } catch (error) {
    console.warn('[Extract Dominant] Failed to extract color from canvas', error);
    return null;
  }
}

/**
 * Calculate HSL distance between two colors (0-100 scale)
 * Used for determining if a pixel is "close enough" to the dominant color
 * @param color1 - First color (hex, rgb, etc.)
 * @param color2 - Second color (hex, rgb, etc.)
 * @returns Distance score (0 = identical, 100 = maximum difference)
 */
export function getHSLDistance(color1: string, color2: string): number {
  const c1 = tinycolor(color1);
  const c2 = tinycolor(color2);
  
  if (!c1.isValid() || !c2.isValid()) return 100;
  
  const hsl1 = c1.toHsl();
  const hsl2 = c2.toHsl();
  
  // Hue distance (circular, 0-180 normalized to 0-1)
  let hueDiff = Math.abs(hsl1.h - hsl2.h);
  if (hueDiff > 180) hueDiff = 360 - hueDiff;
  const hueDistance = hueDiff / 180; // 0-1
  
  // Saturation distance (0-1)
  const satDistance = Math.abs(hsl1.s - hsl2.s);
  
  // Lightness distance (0-1)
  const lightDistance = Math.abs(hsl1.l - hsl2.l);
  
  // Weighted combination (hue is most important for color matching)
  // Hue: 60%, Saturation: 25%, Lightness: 15%
  const distance = (hueDistance * 0.6 + satDistance * 0.25 + lightDistance * 0.15) * 100;
  
  return distance;
}

/**
 * Check if a pixel color is within tolerance of the dominant color
 * OPTIMIZED: Uses pure math functions instead of tinycolor
 * @param pixelR - Red component (0-255)
 * @param pixelG - Green component (0-255)
 * @param pixelB - Blue component (0-255)
 * @param dominantHSL - Pre-computed HSL of dominant color
 * @param tolerance - HSL distance tolerance (default 12, like backend)
 * @returns True if pixel should be transformed
 */
export function isPixelWithinTolerance(
  pixelR: number,
  pixelG: number,
  pixelB: number,
  dominantHSL: { h: number; s: number; l: number },
  tolerance: number = 15
): boolean {
  // Inline RGB to HSL conversion for performance (no tinycolor)
  const rN = pixelR / 255;
  const gN = pixelG / 255;
  const bN = pixelB / 255;

  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  
  // Skip very dark or very light pixels (fast reject)
  if (l < 0.05 || l > 0.95) return false;
  
  // Calculate saturation
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  }
  
  // Skip very desaturated pixels (grays)
  if (s < 0.08) return false;
  
  // Calculate hue only if we passed all fast-reject checks
  let h = 0;
  if (max !== min) {
    const d = max - min;
    switch (max) {
      case rN:
        h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6;
        break;
      case gN:
        h = ((bN - rN) / d + 2) / 6;
        break;
      default:
        h = ((rN - gN) / d + 4) / 6;
        break;
    }
    h *= 360;
  }
  
  // Hue distance (circular)
  let hueDiff = Math.abs(h - dominantHSL.h);
  if (hueDiff > 180) hueDiff = 360 - hueDiff;
  
  // For well-saturated colors, primarily check hue
  if (hueDiff > tolerance) return false;
  
  // Also check saturation isn't wildly different
  const satDiff = Math.abs(s - dominantHSL.s) * 100;
  if (satDiff > tolerance * 3) return false; // More lenient on saturation
  
  return true;
}

/**
 * Apply HSL transformation to a single pixel using optimized BLEND strategy
 * OPTIMIZED: Uses pure math functions instead of tinycolor
 * 
 * Configuration tested and approved via /test-hsl page:
 * - tolerance: 30
 * - saturationMode: blend (70% brand + 30% pixel)
 * - saturationBoost: -15%
 * - lightnessMode: blend with lightnessBlend: 0 (preserve original)
 * - hueMode: replace (brand hue directly)
 * 
 * @param r - Red (0-255)
 * @param g - Green (0-255) 
 * @param b - Blue (0-255)
 * @param brandHSL - Target brand color HSL
 * @param dominantHSL - Original dominant color HSL (for relative calculations)
 * @returns Transformed RGB values
 */
export function transformPixelTowardsBrand(
  r: number,
  g: number,
  b: number,
  brandHSL: { h: number; s: number; l: number },
  dominantHSL: { h: number; s: number; l: number }
): { r: number; g: number; b: number } {
  // Inline RGB to HSL conversion for performance (no tinycolor)
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;

  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;
  
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  }
  
  // CONFIG (tested via /test-hsl):
  const SATURATION_BOOST = -0.15; // -15%
  const MIN_SAT_THRESHOLD = 0.15;
  const LIGHTNESS_BLEND = 0; // 0 = preserve pixel lightness, 1 = use brand lightness
  
  // 1. HUE: Replace with brand hue directly (prevents color drift)
  const newHue = brandHSL.h;
  
  // 2. SATURATION: Blend mode (70% brand + 30% pixel) with boost
  let newSat: number;
  if (s < MIN_SAT_THRESHOLD) {
    // Very desaturated pixel - scale down proportionally to preserve grays
    newSat = brandHSL.s * (s / MIN_SAT_THRESHOLD);
  } else {
    // Blend: 70% brand saturation + 30% pixel saturation + boost
    newSat = s * 0.3 + brandHSL.s * 0.7 + SATURATION_BOOST;
  }
  newSat = Math.max(0, Math.min(1, newSat));
  
  // 3. LIGHTNESS: Blend mode with lightnessBlend = 0 (preserve original pixel lightness)
  const newLight = Math.max(0.02, Math.min(0.98, 
    l * (1 - LIGHTNESS_BLEND) + brandHSL.l * LIGHTNESS_BLEND
  ));
  
  // Inline HSL to RGB conversion for performance (no tinycolor)
  if (newSat === 0) {
    const gray = Math.round(newLight * 255);
    return { r: gray, g: gray, b: gray };
  }

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  const q = newLight < 0.5 ? newLight * (1 + newSat) : newLight + newSat - newLight * newSat;
  const p = 2 * newLight - q;
  const hNorm = newHue / 360;

  return {
    r: Math.round(hue2rgb(p, q, hNorm + 1/3) * 255),
    g: Math.round(hue2rgb(p, q, hNorm) * 255),
    b: Math.round(hue2rgb(p, q, hNorm - 1/3) * 255)
  };
}

/**
 * DEPRECATED: Old delta-based transformation (causes color drift)
 * OPTIMIZED: Uses pure math functions instead of tinycolor
 * Apply HSL transformation to a single pixel
 * @param r - Red (0-255)
 * @param g - Green (0-255) 
 * @param b - Blue (0-255)
 * @param delta - HSL delta to apply
 * @returns Transformed RGB values
 */
export function transformPixelHSL(
  r: number,
  g: number,
  b: number,
  delta: HSLDelta
): { r: number; g: number; b: number } {
  // Inline RGB to HSL conversion (no tinycolor)
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;

  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  let l = (max + min) / 2;
  
  let s = 0;
  let h = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rN:
        h = ((gN - bN) / d + (gN < bN ? 6 : 0)) / 6;
        break;
      case gN:
        h = ((bN - rN) / d + 2) / 6;
        break;
      default:
        h = ((rN - gN) / d + 4) / 6;
        break;
    }
    h *= 360;
  }
  
  // Apply deltas
  let newHue = (h + delta.deltaHue) % 360;
  if (newHue < 0) newHue += 360;
  
  const newSat = Math.max(0, Math.min(1, s + delta.deltaSat / 100));
  const newLight = Math.max(0, Math.min(1, l + delta.deltaLight / 100));
  
  // Inline HSL to RGB conversion (no tinycolor)
  if (newSat === 0) {
    const gray = Math.round(newLight * 255);
    return { r: gray, g: gray, b: gray };
  }

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  const q = newLight < 0.5 ? newLight * (1 + newSat) : newLight + newSat - newLight * newSat;
  const p = 2 * newLight - q;
  const hNorm = newHue / 360;

  return {
    r: Math.round(hue2rgb(p, q, hNorm + 1/3) * 255),
    g: Math.round(hue2rgb(p, q, hNorm) * 255),
    b: Math.round(hue2rgb(p, q, hNorm - 1/3) * 255)
  };
}

/**
 * PERFORMANCE CONSTANTS for HSL transformation
 */
const HSL_TRANSFORM_CONFIG = {
  // Max pixels to process directly (500k = 707x707 or similar)
  MAX_DIRECT_PIXELS: 500000,
  // Target size for downscaled processing (much faster)
  DOWNSCALE_TARGET_PIXELS: 250000, // 500x500 equivalent
  // Minimum size to maintain quality
  MIN_DIMENSION: 100,
  // Sample-based early exit
  SAMPLE_THRESHOLD: 100000,
  SAMPLE_SIZE: 2500,
  MIN_MATCH_PERCENT: 1,
};

/**
 * Downscale an image element to a smaller canvas for faster processing
 * Uses bilinear interpolation for better quality
 * 
 * @param imageElement - Source image or canvas
 * @param targetPixels - Target total pixels (width * height)
 * @returns Object with downscaled canvas and scale factor
 */
function downscaleForProcessing(
  imageElement: HTMLImageElement | HTMLCanvasElement,
  targetPixels: number
): { canvas: HTMLCanvasElement; scaleFactor: number } {
  // Get source dimensions
  let srcWidth: number, srcHeight: number;
  if (imageElement instanceof HTMLImageElement) {
    srcWidth = imageElement.naturalWidth || imageElement.width;
    srcHeight = imageElement.naturalHeight || imageElement.height;
  } else {
    srcWidth = imageElement.width;
    srcHeight = imageElement.height;
  }
  
  const srcPixels = srcWidth * srcHeight;
  
  // Calculate scale factor
  const scaleFactor = Math.sqrt(targetPixels / srcPixels);
  const newWidth = Math.max(HSL_TRANSFORM_CONFIG.MIN_DIMENSION, Math.round(srcWidth * scaleFactor));
  const newHeight = Math.max(HSL_TRANSFORM_CONFIG.MIN_DIMENSION, Math.round(srcHeight * scaleFactor));
  
  // Create downscaled canvas
  const canvas = document.createElement('canvas');
  canvas.width = newWidth;
  canvas.height = newHeight;
  
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx) {
    // Use high quality interpolation for downscale
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(imageElement, 0, 0, newWidth, newHeight);
  }
  
  return { canvas, scaleFactor };
}

/**
 * Upscale a canvas back to original dimensions
 * 
 * @param processedCanvas - Canvas with processed image
 * @param targetWidth - Target width
 * @param targetHeight - Target height
 * @returns Upscaled canvas
 */
function upscaleToOriginal(
  processedCanvas: HTMLCanvasElement,
  targetWidth: number,
  targetHeight: number
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // Use high quality interpolation for upscale
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(processedCanvas, 0, 0, targetWidth, targetHeight);
  }
  
  return canvas;
}

/**
 * Apply selective HSL transformation to an image (only pixels near dominant color)
 * This replicates the backend's tolerance-based color transformation
 * 
 * OPTIMIZED VERSION (FASE 3):
 * - Downscales large images before processing for 4-16x speedup
 * - Uses sampling for large images to determine if transformation is needed
 * - Skips images with <1% matching pixels (early exit)
 * - Logs performance metrics
 * 
 * @param imageElement - Image or canvas element to transform
 * @param dominantColor - The dominant color to match (hex)
 * @param delta - HSL delta to apply
 * @param tolerance - Color distance tolerance (default 12, like backend)
 * @returns Canvas with transformed image
 */
export function applySelectiveHSLTransform(
  imageElement: HTMLImageElement | HTMLCanvasElement,
  dominantColor: string,
  delta: HSLDelta,
  tolerance: number = 12
): HTMLCanvasElement {
  const startTime = performance.now();
  
  // Get original dimensions
  let originalWidth: number, originalHeight: number;
  if (imageElement instanceof HTMLImageElement) {
    originalWidth = imageElement.naturalWidth || imageElement.width;
    originalHeight = imageElement.naturalHeight || imageElement.height;
  } else {
    originalWidth = imageElement.width;
    originalHeight = imageElement.height;
  }
  
  const originalPixels = originalWidth * originalHeight;
  
  // Pre-compute dominant and brand HSL
  const dominant = tinycolor(dominantColor);
  const dominantHSL = dominant.toHsl();
  
  // Calculate brand HSL from delta (dominant + delta = brand)
  let brandHue = (dominantHSL.h + delta.deltaHue) % 360;
  if (brandHue < 0) brandHue += 360;
  const brandSat = Math.max(0, Math.min(1, dominantHSL.s + delta.deltaSat / 100));
  const brandLight = Math.max(0, Math.min(1, dominantHSL.l + delta.deltaLight / 100));
  const brandHSL = { h: brandHue, s: brandSat, l: brandLight };
  
  // ============================================================
  // OPTIMIZATION: Downscale large images for faster processing
  // Process at lower resolution, then upscale back
  // ============================================================
  let workingCanvas: HTMLCanvasElement;
  let needsUpscale = false;
  let scaleFactor = 1;
  
  if (originalPixels > HSL_TRANSFORM_CONFIG.MAX_DIRECT_PIXELS) {
    const downscaled = downscaleForProcessing(imageElement, HSL_TRANSFORM_CONFIG.DOWNSCALE_TARGET_PIXELS);
    workingCanvas = downscaled.canvas;
    scaleFactor = downscaled.scaleFactor;
    needsUpscale = true;
    
    console.log(`⚡ [SelectiveHSL] Downscaled for processing:`, {
      original: `${originalWidth}x${originalHeight} (${originalPixels.toLocaleString()} px)`,
      working: `${workingCanvas.width}x${workingCanvas.height} (${(workingCanvas.width * workingCanvas.height).toLocaleString()} px)`,
      scaleFactor: scaleFactor.toFixed(3),
      speedup: `~${Math.round(1 / (scaleFactor * scaleFactor))}x`
    });
  } else {
    // Create canvas at original size
    workingCanvas = document.createElement('canvas');
    workingCanvas.width = originalWidth;
    workingCanvas.height = originalHeight;
    const ctx = workingCanvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      ctx.drawImage(imageElement, 0, 0);
    }
  }
  
  const ctx = workingCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    console.error('[SelectiveHSL] Failed to get canvas context');
    return workingCanvas;
  }
  
  const workingPixels = workingCanvas.width * workingCanvas.height;
  
  // Get image data
  const imageData = ctx.getImageData(0, 0, workingCanvas.width, workingCanvas.height);
  const data = imageData.data;
  
  // ============================================================
  // OPTIMIZATION: Sample-based early exit
  // If <1% of sampled pixels match dominant color, skip transformation
  // ============================================================
  if (workingPixels > HSL_TRANSFORM_CONFIG.SAMPLE_THRESHOLD) {
    let sampleMatches = 0;
    const step = Math.floor(data.length / 4 / HSL_TRANSFORM_CONFIG.SAMPLE_SIZE) * 4;
    
    for (let i = 0; i < data.length && sampleMatches < HSL_TRANSFORM_CONFIG.SAMPLE_SIZE; i += step) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      
      if (a >= 10 && isPixelWithinTolerance(r, g, b, dominantHSL, tolerance)) {
        sampleMatches++;
      }
    }
    
    const sampleMatchPercent = (sampleMatches / HSL_TRANSFORM_CONFIG.SAMPLE_SIZE) * 100;
    
    if (sampleMatchPercent < HSL_TRANSFORM_CONFIG.MIN_MATCH_PERCENT) {
      const skipTime = performance.now() - startTime;
      console.log(`⚡ [SelectiveHSL] SKIPPED (early exit):`, {
        originalSize: `${originalWidth}x${originalHeight}`,
        workingSize: `${workingCanvas.width}x${workingCanvas.height}`,
        sampleMatchPercent: sampleMatchPercent.toFixed(2) + '%',
        timeMs: skipTime.toFixed(1),
        reason: `<${HSL_TRANSFORM_CONFIG.MIN_MATCH_PERCENT}% pixels match dominant color`
      });
      
      // Return original size canvas (not working canvas)
      if (needsUpscale) {
        return upscaleToOriginal(workingCanvas, originalWidth, originalHeight);
      }
      return workingCanvas;
    }
  }
  
  console.log(`🎨 [SelectiveHSL] Processing:`, {
    original: `${originalWidth}x${originalHeight}`,
    working: `${workingCanvas.width}x${workingCanvas.height}`,
    dominant: { h: dominantHSL.h.toFixed(1), s: (dominantHSL.s * 100).toFixed(1) + '%' },
    brand: { h: brandHSL.h.toFixed(1), s: (brandHSL.s * 100).toFixed(1) + '%' },
    tolerance
  });
  
  let transformedCount = 0;
  let skippedCount = 0;
  
  // Process each pixel
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    
    // Skip transparent pixels
    if (a < 10) {
      skippedCount++;
      continue;
    }
    
    // Check if pixel is within tolerance of dominant color
    if (isPixelWithinTolerance(r, g, b, dominantHSL, tolerance)) {
      const transformed = transformPixelTowardsBrand(r, g, b, brandHSL, dominantHSL);
      data[i] = transformed.r;
      data[i + 1] = transformed.g;
      data[i + 2] = transformed.b;
      transformedCount++;
    } else {
      skippedCount++;
    }
  }
  
  // Put transformed data back
  ctx.putImageData(imageData, 0, 0);
  
  // ============================================================
  // UPSCALE: If we downscaled, scale back to original dimensions
  // ============================================================
  let resultCanvas = workingCanvas;
  if (needsUpscale) {
    resultCanvas = upscaleToOriginal(workingCanvas, originalWidth, originalHeight);
  }
  
  const totalTime = performance.now() - startTime;
  
  console.log(`🎨 [SelectiveHSL] Complete:`, {
    original: `${originalWidth}x${originalHeight} (${originalPixels.toLocaleString()} px)`,
    processed: `${workingCanvas.width}x${workingCanvas.height} (${workingPixels.toLocaleString()} px)`,
    transformedPixels: transformedCount.toLocaleString(),
    transformedPercent: ((transformedCount / workingPixels) * 100).toFixed(1) + '%',
    timeMs: totalTime.toFixed(1),
    pixelsPerMs: Math.round(workingPixels / totalTime),
    wasDownscaled: needsUpscale,
    speedup: needsUpscale ? `~${Math.round(originalPixels / workingPixels)}x` : '1x'
  });
  
  return resultCanvas;
}

/**
 * ASYNC VERSION: Apply selective HSL transformation with chunking to avoid UI blocking
 * For very large images (>500k pixels), this processes in chunks with requestIdleCallback
 * 
 * @param imageElement - Image or canvas element to transform
 * @param dominantColor - The dominant color to match (hex)
 * @param delta - HSL delta to apply
 * @param tolerance - Color distance tolerance
 * @param onProgress - Optional progress callback (0-100)
 * @returns Promise<Canvas> with transformed image
 */
export async function applySelectiveHSLTransformAsync(
  imageElement: HTMLImageElement | HTMLCanvasElement,
  dominantColor: string,
  delta: HSLDelta,
  tolerance: number = 12,
  onProgress?: (percent: number) => void
): Promise<HTMLCanvasElement> {
  const startTime = performance.now();
  
  // Create canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  
  if (!ctx) {
    console.error('[SelectiveHSL Async] Failed to get canvas context');
    return canvas;
  }
  
  // Set canvas size
  if (imageElement instanceof HTMLImageElement) {
    canvas.width = imageElement.naturalWidth || imageElement.width;
    canvas.height = imageElement.naturalHeight || imageElement.height;
  } else {
    canvas.width = imageElement.width;
    canvas.height = imageElement.height;
  }
  
  const totalPixels = canvas.width * canvas.height;
  
  // Draw original image
  ctx.drawImage(imageElement, 0, 0);
  
  // For small images (<500k pixels), use sync version
  if (totalPixels < 500000) {
    console.log(`⚡ [SelectiveHSL Async] Small image (${totalPixels} pixels), using sync path`);
    return applySelectiveHSLTransform(imageElement, dominantColor, delta, tolerance);
  }
  
  // Pre-compute colors
  const dominant = tinycolor(dominantColor);
  const dominantHSL = dominant.toHsl();
  
  let brandHue = (dominantHSL.h + delta.deltaHue) % 360;
  if (brandHue < 0) brandHue += 360;
  const brandSat = Math.max(0, Math.min(1, dominantHSL.s + delta.deltaSat / 100));
  const brandLight = Math.max(0, Math.min(1, dominantHSL.l + delta.deltaLight / 100));
  const brandHSL = { h: brandHue, s: brandSat, l: brandLight };
  
  // Get image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  
  // ============================================================
  // CHUNKED PROCESSING: Process in chunks with yielding to UI
  // ============================================================
  const CHUNK_SIZE = 100000; // Process 100k pixels per chunk
  const totalChunks = Math.ceil(data.length / 4 / CHUNK_SIZE);
  
  let transformedCount = 0;
  let skippedCount = 0;
  
  console.log(`🎨 [SelectiveHSL Async] Starting chunked processing:`, {
    totalPixels,
    chunkSize: CHUNK_SIZE,
    totalChunks,
    canvasSize: `${canvas.width}x${canvas.height}`
  });
  
  // Process in chunks
  for (let chunk = 0; chunk < totalChunks; chunk++) {
    const startIdx = chunk * CHUNK_SIZE * 4;
    const endIdx = Math.min(startIdx + CHUNK_SIZE * 4, data.length);
    
    // Process this chunk
    for (let i = startIdx; i < endIdx; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      
      if (a < 10) {
        skippedCount++;
        continue;
      }
      
      if (isPixelWithinTolerance(r, g, b, dominantHSL, tolerance)) {
        const transformed = transformPixelTowardsBrand(r, g, b, brandHSL, dominantHSL);
        data[i] = transformed.r;
        data[i + 1] = transformed.g;
        data[i + 2] = transformed.b;
        transformedCount++;
      } else {
        skippedCount++;
      }
    }
    
    // Report progress
    const progress = Math.round(((chunk + 1) / totalChunks) * 100);
    onProgress?.(progress);
    
    // Yield to UI between chunks (allow repaints)
    if (chunk < totalChunks - 1) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }
  
  // Put transformed data back
  ctx.putImageData(imageData, 0, 0);
  
  const totalTime = performance.now() - startTime;
  
  console.log(`🎨 [SelectiveHSL Async] Transformation complete:`, {
    canvasSize: `${canvas.width}x${canvas.height}`,
    totalPixels,
    transformedPixels: transformedCount,
    skippedPixels: skippedCount,
    transformedPercent: ((transformedCount / totalPixels) * 100).toFixed(1) + '%',
    timeMs: totalTime.toFixed(1),
    pixelsPerMs: Math.round(totalPixels / totalTime),
    chunks: totalChunks
  });
  
  return canvas;
}

/**
 * Format file size from bytes to human-readable format
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "1.5 MB", "300 KB")
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Format date to Brazilian format (dd/mm/yyyy)
 * @param date - Date string or Date object
 * @returns Formatted date string
 */
export function formatDateBR(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(d.getTime())) return 'Data inválida';

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
}

/**
 * Truncate text to a maximum length
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to add (default: '...')
 * @returns Truncated text
 */
export function truncate(text: string, maxLength: number, suffix: string = '...'): string {
  if (text.length <= maxLength) return text;

  return text.slice(0, maxLength - suffix.length) + suffix;
}

/**
 * Generate random ID
 * @returns Random ID string
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Debounce function
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 * @param value - Value to check
 * @returns True if empty
 */
export function isEmpty(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Convert relative backend URL to absolute URL
 * @param path - Relative path from backend (e.g., "/uploads/avatars/logo.png")
 * @returns Absolute URL pointing to backend server
 */
export function getBackendImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;

  // Se já for uma URL completa (blob: ou http), retornar como está
  if (path.startsWith('blob:') || path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // Garantir que o path começa com barra
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Sempre usar URL absoluta - Next.js Image com unoptimized precisa de URL acessível
  // O nginx redireciona /uploads/* para o backend automaticamente
  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? 'http://localhost:4001';
  return `${backendUrl}${normalizedPath}`;
}

// ==================== CUSTOM COLOR CONFIG TYPES ====================

/**
 * HSL adjustment configuration - matches test-hsl page structure
 */
export interface HSLAdjustConfig {
  globalTolerance: number; // 0-100, fallback tolerance (degrees)
  saturation: number; // -100 to +100 (legacy, maps to saturationBoost * 100)
  lightness: number; // -100 to +100 (legacy, maps to lightnessBlend)
  vividness: number; // -100 to +100 (affects saturation curve)
  // Advanced modes from test-hsl
  saturationMode?: 'vivid' | 'proportional' | 'max' | 'blend' | 'preserve';
  saturationBoost?: number; // -1 to 1 (e.g., 0.15 = +15%)
  lightnessMode?: 'preserve' | 'brand' | 'blend';
  lightnessBlend?: number; // 0-1 blend factor
  hueMode?: 'replace' | 'blend';
  hueBlend?: number; // 0-1 blend factor (1 = full replace)
  featherScale?: number; // 0-2, edge softness factor
  dissolveEdges?: boolean; // aggressive dissolve for high contrast edges
  mtToneShift?: number; // MT mode: tone shift in degrees
  mtFeather?: number; // MT mode: feather amount
}

/**
 * Custom color configuration structure for Color Adjuster
 */
export interface CustomColorConfig {
  enabled: boolean;
  targetColor: string; // Brand primary color (hex)
  dominantColors: Array<{
    hex: string;
    tolerance: number; // 0-100, hue tolerance in degrees
  }>;
  excludeColors: Array<{
    hex: string;
    tolerance: number; // 0-100, tolerance for excluded color matching
  }>;
  hslAdjust: HSLAdjustConfig;
  // Saturation thresholds - only pixels within this range are transformed
  minSatThreshold?: number; // 0-1, default 0 (transforms all saturated)
  maxSatThreshold?: number; // 0-1, default 1 (transforms all saturated)
  // Lightness thresholds - only pixels within this range are transformed
  minLightThreshold?: number; // 0-1, default 0
  maxLightThreshold?: number; // 0-1, default 1
}

/**
 * Check if a pixel should be transformed based on custom color config
 * @param pixelR - Red component (0-255)
 * @param pixelG - Green component (0-255)
 * @param pixelB - Blue component (0-255)
 * @param config - Custom color configuration
 * @returns Object with match info: { shouldTransform, matchedDominant, tolerance }
 */
export function shouldTransformPixelWithConfig(
  pixelR: number,
  pixelG: number,
  pixelB: number,
  config: CustomColorConfig
): { shouldTransform: boolean; matchedDominant: string | null; tolerance: number } {
  const pixelColor = tinycolor({ r: pixelR, g: pixelG, b: pixelB });
  const pixelHSL = pixelColor.toHsl();
  const pixelHex = pixelColor.toHexString().toUpperCase();
  
  // Get saturation thresholds from config (default: 0.08 to 1.0)
  const minSat = config.minSatThreshold ?? 0.08;
  const maxSat = config.maxSatThreshold ?? 1.0;
  
  // Skip pixels outside saturation range
  if (pixelHSL.s < minSat || pixelHSL.s > maxSat) {
    return { shouldTransform: false, matchedDominant: null, tolerance: 0 };
  }
  
  // Skip very dark or very light pixels
  if (pixelHSL.l < 0.05 || pixelHSL.l > 0.95) {
    return { shouldTransform: false, matchedDominant: null, tolerance: 0 };
  }
  
  // Check if pixel is in exclude list (with individual tolerance)
  if (config.excludeColors && config.excludeColors.length > 0) {
    for (const excludeEntry of config.excludeColors) {
      // Support both old format (string) and new format (object with tolerance)
      const excludeHex = typeof excludeEntry === 'string' ? excludeEntry : excludeEntry.hex;
      const excludeTolerance = typeof excludeEntry === 'string' ? 15 : (excludeEntry.tolerance ?? 15);
      
      const excludeColor = tinycolor(excludeHex);
      if (!excludeColor.isValid()) continue;
      
      // Check if pixel is close to excluded color using configurable tolerance
      const excludeRgb = excludeColor.toRgb();
      const rDiff = Math.abs(pixelR - excludeRgb.r);
      const gDiff = Math.abs(pixelG - excludeRgb.g);
      const bDiff = Math.abs(pixelB - excludeRgb.b);
      
      if (rDiff < excludeTolerance && gDiff < excludeTolerance && bDiff < excludeTolerance) {
        return { shouldTransform: false, matchedDominant: null, tolerance: 0 };
      }
    }
  }
  
  // Check each dominant color with its individual tolerance
  for (const dominant of config.dominantColors) {
    const dominantColor = tinycolor(dominant.hex);
    if (!dominantColor.isValid()) continue;
    
    const dominantHSL = dominantColor.toHsl();
    
    // Hue distance (circular)
    let hueDiff = Math.abs(pixelHSL.h - dominantHSL.h);
    if (hueDiff > 180) hueDiff = 360 - hueDiff;
    
    // Check hue within individual tolerance
    if (hueDiff <= dominant.tolerance) {
      // Also check saturation isn't wildly different (3x tolerance for leniency)
      const satDiff = Math.abs(pixelHSL.s - dominantHSL.s) * 100;
      if (satDiff <= dominant.tolerance * 3) {
        return { 
          shouldTransform: true, 
          matchedDominant: dominant.hex, 
          tolerance: dominant.tolerance 
        };
      }
    }
  }
  
  return { shouldTransform: false, matchedDominant: null, tolerance: 0 };
}

/**
 * Transform pixel to target color with custom HSL adjustments
 * @param r - Red (0-255)
 * @param g - Green (0-255)
 * @param b - Blue (0-255)
 * @param targetHSL - Target (brand) color HSL
 * @param config - Custom color configuration with HSL adjustments
 * @returns Transformed RGB values
 */
export function transformPixelWithConfig(
  r: number,
  g: number,
  b: number,
  targetHSL: { h: number; s: number; l: number },
  config: CustomColorConfig
): { r: number; g: number; b: number } {
  const color = tinycolor({ r, g, b });
  const pixelHSL = color.toHsl();
  
  // Base saturation calculation (70% target + 30% pixel)
  const baseSat = pixelHSL.s * 0.3 + targetHSL.s * 0.7;
  
  // Apply saturation adjustment from config (-100 to +100 → -1 to +1)
  let newSat = baseSat + (config.hslAdjust.saturation / 100);
  
  // Apply vividness curve (amplifies saturation for already saturated colors)
  if (config.hslAdjust.vividness !== 0) {
    const vividFactor = config.hslAdjust.vividness / 100;
    // Vividness > 0: boost saturation curve, < 0: flatten it
    newSat = newSat + (newSat * vividFactor * pixelHSL.s);
  }
  newSat = Math.max(0, Math.min(1, newSat));
  
  // Preserve pixel lightness with optional adjustment
  let newLight = pixelHSL.l + (config.hslAdjust.lightness / 100);
  newLight = Math.max(0.02, Math.min(0.98, newLight));
  
  // Use target hue (brand color)
  const newHue = targetHSL.h;
  
  const newColor = tinycolor({ h: newHue, s: newSat, l: newLight });
  const rgb = newColor.toRgb();
  
  return { r: rgb.r, g: rgb.g, b: rgb.b };
}

/**
 * Apply multi-color selective HSL transformation using custom config
 * This is used by Color Adjuster to transform images with multiple dominant colors
 * 
 * OPTIMIZED VERSION: Uses precomputed config and pure math functions
 * to avoid tinycolor overhead in pixel loops.
 * 
 * @param imageElement - Image or canvas element to transform
 * @param config - Custom color configuration from Color Adjuster
 * @returns Canvas with transformed image
 */
export function applyMultiColorTransform(
  imageElement: HTMLImageElement | HTMLCanvasElement,
  config: CustomColorConfig
): HTMLCanvasElement {
  // Create canvas to work with
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  
  if (!ctx) {
    console.error('[MultiColorTransform] Failed to get canvas context');
    return canvas;
  }
  
  // Set canvas size
  if (imageElement instanceof HTMLImageElement) {
    canvas.width = imageElement.naturalWidth || imageElement.width;
    canvas.height = imageElement.naturalHeight || imageElement.height;
  } else {
    canvas.width = imageElement.width;
    canvas.height = imageElement.height;
  }
  
  // Draw original image
  ctx.drawImage(imageElement, 0, 0);
  
  // If config is disabled, return original
  if (!config.enabled) {
    console.log('[MultiColorTransform] Config disabled, returning original');
    return canvas;
  }
  
  // Get image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  
  // PRE-COMPUTE all color conversions ONCE before the loop
  const precomputed = precomputeColorConfig(
    config.targetColor,
    config.dominantColors,
    config.excludeColors,
    config.hslAdjust,
    config.minSatThreshold ?? 0.08,
    config.maxSatThreshold ?? 1.0
  );
  
  if (!precomputed) {
    console.error('[MultiColorTransform] Failed to precompute config');
    return canvas;
  }
  
  console.log(`🎨 [MultiColorTransform] Starting OPTIMIZED transformation:`, {
    targetColor: config.targetColor,
    targetHSL: precomputed.targetHSL,
    dominantColors: config.dominantColors.map(d => `${d.hex} (tol: ${d.tolerance})`),
    excludeColors: config.excludeColors,
    hslAdjust: config.hslAdjust,
    pixelCount: data.length / 4
  });
  
  const startTime = performance.now();
  let transformedCount = 0;
  let skippedCount = 0;
  const matchedDominants: Record<string, number> = {};
  
  // Process each pixel using OPTIMIZED functions (no tinycolor in loop)
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    
    // Skip transparent pixels
    if (a < 10) {
      skippedCount++;
      continue;
    }
    
    // Check if pixel should be transformed (FAST version - no tinycolor)
    const matchResult = shouldTransformPixelFast(r, g, b, precomputed);
    
    if (matchResult.shouldTransform && matchResult.matchedDominant) {
      // Transform this pixel (FAST version - no tinycolor)
      const transformed = transformPixelFast(r, g, b, precomputed);
      data[i] = transformed.r;
      data[i + 1] = transformed.g;
      data[i + 2] = transformed.b;
      // Alpha stays the same
      transformedCount++;
      
      // Track which dominant color matched
      const dominantKey = matchResult.matchedDominant;
      matchedDominants[dominantKey] = (matchedDominants[dominantKey] || 0) + 1;
    } else {
      skippedCount++;
    }
  }
  
  // Put transformed data back
  ctx.putImageData(imageData, 0, 0);
  
  const elapsed = performance.now() - startTime;
  console.log(`🎨 [MultiColorTransform] OPTIMIZED transformation complete:`, {
    canvasSize: `${canvas.width}x${canvas.height}`,
    totalPixels: data.length / 4,
    transformedPixels: transformedCount,
    skippedPixels: skippedCount,
    transformedPercent: ((transformedCount / (data.length / 4)) * 100).toFixed(1) + '%',
    matchedDominants,
    elapsedMs: elapsed.toFixed(1)
  });
  
  return canvas;
}

/**
 * Extract the average color from non-transparent pixels of an image/canvas
 * Used to get the actual rendered color from a transformed PNG (for PNG→IText conversion)
 * 
 * @param element - HTMLImageElement, HTMLCanvasElement, or Fabric.FabricImage
 * @param options - Options for color extraction
 * @returns Hex color string of the average color, or null if failed
 */
export function extractAverageColorFromImage(
  element: HTMLImageElement | HTMLCanvasElement | { getElement: () => HTMLImageElement | HTMLCanvasElement },
  options: {
    minAlpha?: number;           // Minimum alpha to consider (default: 128)
    sampleRate?: number;         // Sample every Nth pixel (default: 4 for performance)
    excludeNeutral?: boolean;    // Exclude very neutral colors (whites/blacks/grays)
    neutralThreshold?: number;   // Saturation threshold for neutral (default: 0.08)
  } = {}
): string | null {
  const {
    minAlpha = 128,
    sampleRate = 4,
    excludeNeutral = true,
    neutralThreshold = 0.08
  } = options;
  
  try {
    // Get the actual HTML element
    let htmlElement: HTMLImageElement | HTMLCanvasElement;
    if ('getElement' in element && typeof element.getElement === 'function') {
      htmlElement = element.getElement();
    } else {
      htmlElement = element as HTMLImageElement | HTMLCanvasElement;
    }
    
    // Create temporary canvas to read pixels
    const canvas = document.createElement('canvas');
    let width: number, height: number;
    
    if (htmlElement instanceof HTMLImageElement) {
      width = htmlElement.naturalWidth || htmlElement.width;
      height = htmlElement.naturalHeight || htmlElement.height;
    } else {
      width = htmlElement.width;
      height = htmlElement.height;
    }
    
    if (width <= 0 || height <= 0) {
      console.warn('[extractAverageColor] Invalid dimensions:', { width, height });
      return null;
    }
    
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      console.warn('[extractAverageColor] Failed to get canvas context');
      return null;
    }
    
    ctx.drawImage(htmlElement, 0, 0);
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    
    let totalR = 0, totalG = 0, totalB = 0;
    let pixelCount = 0;
    
    // Sample pixels with specified rate for performance
    for (let i = 0; i < data.length; i += 4 * sampleRate) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      
      // Skip transparent pixels
      if (a < minAlpha) continue;
      
      // Optionally exclude neutral colors (to get actual colored pixels)
      if (excludeNeutral) {
        // Quick saturation check - calculate inline for performance
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const l = (max + min) / 510; // Normalized lightness (0-1)
        
        // Skip very dark or very light pixels
        if (l < 0.05 || l > 0.95) continue;
        
        // Calculate saturation
        const d = max - min;
        const s = l > 0.5 ? d / (510 - max - min) : d / (max + min);
        
        // Skip neutral (low saturation) pixels
        if (s < neutralThreshold) continue;
      }
      
      totalR += r;
      totalG += g;
      totalB += b;
      pixelCount++;
    }
    
    if (pixelCount === 0) {
      console.log('[extractAverageColor] No colored pixels found, returning null');
      return null;
    }
    
    const avgR = Math.round(totalR / pixelCount);
    const avgG = Math.round(totalG / pixelCount);
    const avgB = Math.round(totalB / pixelCount);
    
    const hexColor = tinycolor({ r: avgR, g: avgG, b: avgB }).toHexString();
    
    console.log(`🎨 [extractAverageColor] Extracted average color:`, {
      sampledPixels: pixelCount,
      averageRGB: `rgb(${avgR}, ${avgG}, ${avgB})`,
      hex: hexColor
    });
    
    return hexColor;
  } catch (error) {
    console.error('[extractAverageColor] Error extracting color:', error);
    return null;
  }
}
